import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import i18n from "../i18n/config";
import {
  getAuthToken,
  getAuthUser,
  isAuthenticated,
  logout as logoutAuth,
  telegramAuth,
  type TelegramAuthResponse,
} from "../services/auth";

declare global {
  interface Window {
    onTelegramAuth?: (user: {
      id: number;
      first_name: string;
      last_name?: string;
      username?: string;
      photo_url?: string;
      auth_date: number;
      hash: string;
      allow_write_to_pm?: number;
    }) => void;
  }
}

export function useTelegramAuth() {
  const [user, setUser] = useState<TelegramAuthResponse["user"] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    // Only run on client-side
    if (typeof window === "undefined") return;

    const token = getAuthToken();
    if (token && !user) {
      const savedUser = getAuthUser();
      if (savedUser) {
        setUser(savedUser);
      }
    }
  }, []);

  useEffect(() => {
    // Only run on client-side
    if (typeof window === "undefined") return;

    const syncAuth = () => {
      const token = getAuthToken();
      if (token) {
        const savedUser = getAuthUser();
        if (savedUser) setUser(savedUser);
      } else {
        setUser(null);
      }
    };

    window.addEventListener("lotin:auth", syncAuth);
    return () => window.removeEventListener("lotin:auth", syncAuth);
  }, []);

  const handleTelegramAuth = useCallback(
    async (telegramUser: {
      id: number;
      first_name: string;
      last_name?: string;
      username?: string;
      photo_url?: string;
      auth_date: number;
      hash: string;
      allow_write_to_pm?: number;
    }) => {
      setIsLoading(true);
      try {
        // Check if user allowed write access
        if (telegramUser.allow_write_to_pm === 0) {
          setAuthError("Bot xabar yuborishi uchun ruxsat bering");
          setIsLoading(false);
          return { success: false, error: "write_access_required" };
        }

        setAuthError(null);

        // Forward the full widget payload (incl. hash) for server-side verification.
        const { allow_write_to_pm: _ignored, ...widgetData } = telegramUser;
        const result = await telegramAuth(widgetData);

        if (result.status === "success" && result.user) {
          setUser(result.user);
          window.dispatchEvent(new Event("lotin:auth"));
          setAuthError(null);
          return { success: true, user: result.user };
        } else {
          let errorMsg = "Authentication failed";
          if (result.message) {
            errorMsg = result.message;
          } else if (result.errors) {
            errorMsg =
              typeof result.errors === "string"
                ? result.errors
                : Object.values(result.errors).flat().join(", ");
          }
          setAuthError(errorMsg);
          return {
            success: false,
            error: errorMsg,
          };
        }
      } catch (error) {
        const errorMsg =
          error instanceof Error ? error.message : "Authentication failed";
        setAuthError(errorMsg);
        return {
          success: false,
          error: errorMsg,
        };
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const logout = useCallback(() => {
    logoutAuth();
    setUser(null);
    window.dispatchEvent(new Event("lotin:auth"));
    toast.success(i18n.t("toast.loggedOut", "Tizimdan chiqdingiz"));
  }, []);

  useEffect(() => {
    // Only run on client-side
    if (typeof window === "undefined") return;

    window.onTelegramAuth = handleTelegramAuth;
    return () => {
      window.onTelegramAuth = undefined;
    };
  }, [handleTelegramAuth]);

  return {
    user,
    isAuthenticated: isAuthenticated(),
    isLoading,
    logout,
    handleTelegramAuth,
    authError,
  };
}
